<template>
  <div>
    <!-- Back Button and Header -->
    <v-btn icon class="back-button" @click="openExitConfirmation">
      <v-icon>mdi-arrow-left</v-icon>
    </v-btn>
    <v-row justify="center" class="my-2">
      <h2 class="red--text font-weight-bold">Add Jobs</h2>
    </v-row>

    <!-- Exit Confirmation Dialog -->
    <v-dialog v-model="exitDialog" max-width="500px">
      <v-card>
        <v-card-title class="headline">Confirm Exit</v-card-title>
        <v-card-text>
          Are you sure you want to exit? Unsaved changes will be lost.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="red" @click="exitWithoutSaving">Exit</v-btn>
          <v-btn @click="exitDialog = false">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Success Dialog -->
    <v-dialog v-model="dialogSuccess" max-width="500px">
      <v-card>
        <v-card-title class="headline">Job Created Successfully</v-card-title>
        <v-card-text>Where would you like to go?</v-card-text>
        <v-card-actions>
          <v-btn color="green" @click="navigateTo('Functions')">Go to Function</v-btn>
          <v-btn color="blue" @click="navigateTo('AllJobs')">Go to All Jobs</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Error Dialog -->
    <v-dialog v-model="dialogError" max-width="500px">
      <v-card>
        <v-card-title class="headline">Submission Error</v-card-title>
        <v-card-text>
          There are missing required fields. Please review the form and fill in all the necessary information before submitting.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="red" @click="dialogError = false">OK</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Main Form -->
    <v-card class="pa-6 add-job-card">
      <v-card-text>
        <h3 class="section-title font-weight-bold">Job Details</h3>
        <v-form ref="form" v-model="valid">
          <v-row dense>
            <!-- Row 1 -->
            <v-col cols="12" md="3" class="pa-2">
              <v-text-field v-model="jobNumber" label="Job Number" dense outlined prepend-inner-icon="mdi-cube-outline" class="montserrat-input" :rules="[rules.required]" readonly></v-text-field>
            </v-col>
            <v-col cols="12" md="3" class="pa-2">
              <v-text-field v-model="jobName" label="Seal Type" dense outlined prepend-inner-icon="mdi-cube-outline" class="montserrat-input" :rules="[rules.required]"></v-text-field>
            </v-col>
            <v-col cols="12" md="3" class="pa-2">
              <v-text-field v-model="ShaftSize" label="Shaft Size" dense outlined prepend-inner-icon="mdi-clock" class="montserrat-input" :rules="[rules.required]"></v-text-field>
            </v-col>
            <v-col cols="12" md="3" class="pa-2">
              <v-autocomplete v-model="customerName" :items="customers" item-text="companyName" item-value="docId" label="Customer Name" dense outlined prepend-inner-icon="mdi-account" class="montserrat-input" :rules="[rules.required]" clearable></v-autocomplete>
            </v-col>
            <!-- Row 2 -->
            <v-col cols="12" md="3" class="pa-2">
              <v-text-field v-model="customerNumber" label="Contact Person" dense outlined prepend-inner-icon="mdi-account" class="montserrat-input" :rules="[rules.required]"></v-text-field>
            </v-col>
            <v-col cols="12" md="3" class="pa-2">
              <v-select v-model="engineer" :items="engineers" label="Engineer" item-text="fullName" item-value="docId" outlined dense prepend-inner-icon="mdi-account" class="montserrat-input" :rules="[rules.required]"></v-select>
            </v-col>
            <v-col cols="12" md="3" class="pa-2">
              <v-select v-model="technician" :items="technicians" label="Technician" item-text="fullName" item-value="docId" outlined dense prepend-inner-icon="mdi-account" class="montserrat-input" :rules="[rules.required]"></v-select>
            </v-col>
            <v-col cols="12" md="3" class="pa-2">
              <v-select v-model="salesperson" :items="salespersons" label="Salesperson" item-text="fullName" item-value="docId" outlined dense prepend-inner-icon="mdi-account" class="montserrat-input" :rules="[rules.required]"></v-select>
            </v-col>
            <!-- Row 3 -->
            <v-col cols="12" md="3" class="pa-2">
              <v-select v-model="urgency" :items="urgencyOptions" label="Priority" dense outlined prepend-inner-icon="mdi-alert" class="montserrat-input" :rules="[rules.required]"></v-select>
            </v-col>
            <v-col cols="12" md="3" class="pa-2">
              <v-text-field v-model="setRate" label="Set Rate" dense outlined prepend-inner-icon="mdi-currency-usd" class="montserrat-input" readonly></v-text-field>
            </v-col>
            <v-col cols="12" md="3" class="pa-2">
              <v-text-field v-model="timeToComplete" label="Labour Hours" dense outlined prepend-inner-icon="mdi-clock" class="montserrat-input" :rules="[rules.required]"></v-text-field>
            </v-col>
            <v-col cols="12" md="3" class="pa-2">
              <v-select v-model="status" :items="statuses" label="Status" outlined dense prepend-inner-icon="mdi-timeline-clock" class="montserrat-input" @change="handleStatusChange" :rules="[rules.required]"></v-select>
            </v-col>
            <!-- Row 4 -->
            <v-col cols="12" md="3" class="pa-2">
              <v-text-field v-model="currentDate" label="Inspection Date" dense outlined prepend-inner-icon="mdi-calendar" class="montserrat-input" readonly></v-text-field>
            </v-col>
            <v-col cols="12" md="3" class="pa-2">
              <v-select v-model="TotalLead" :items="leadTimeOptions" label="Total Lead Time" dense outlined prepend-inner-icon="mdi-clock" class="montserrat-input" :rules="[rules.required]"></v-select>
            </v-col>
            <v-col cols="12" md="3" class="pa-2">
              <v-text-field v-model="serialNumber" label="Serial Number" dense outlined prepend-inner-icon="mdi-link" class="montserrat-input" :rules="[rules.required]"></v-text-field>
            </v-col>
            <!-- Row 5 -->
            <v-col cols="12" md="3" class="pa-2">
              <v-text-field v-model="elastomers" label="Elastomers" dense outlined prepend-inner-icon="mdi-cog" class="montserrat-input" :rules="[rules.required]"></v-text-field>
            </v-col>
            <v-col cols="12" md="3" class="pa-2">
              <v-text-field v-model="ibs" label="IBS Face Material" dense outlined prepend-inner-icon="mdi-cog-outline" class="montserrat-input" :rules="[rules.required]"></v-text-field>
            </v-col>
            <v-col cols="12" md="3" class="pa-2">
              <v-text-field v-model="ibr" label="IBR Face Material" dense outlined prepend-inner-icon="mdi-cog-outline" class="montserrat-input" :rules="[rules.required]"></v-text-field>
            </v-col>
            <v-col cols="12" md="3" class="pa-2">
              <v-text-field v-model="obs" label="OBS Face Material" dense outlined prepend-inner-icon="mdi-cog-outline" class="montserrat-input" :rules="[rules.required]"></v-text-field>
            </v-col>
            <!-- Row 6 -->
            <v-col cols="12" md="3" class="pa-2">
              <v-text-field v-model="obr" label="OBR Face Material" dense outlined prepend-inner-icon="mdi-cog-outline" class="montserrat-input" :rules="[rules.required]"></v-text-field>
            </v-col>
          </v-row>

          <!-- Job Description -->
          <v-row>
            <v-col cols="12" class="pa-2">
              <v-textarea
                v-model="jobDescription"
                label="Job Description"
                outlined
                dense
                prepend-inner-icon="mdi-format-list-bulleted"
                class="montserrat-input"
                style="min-height: 100px;"
                :rules="[rules.required]"
              ></v-textarea>
            </v-col>
          </v-row>

          <!-- Inspection Checklist -->
          <v-row class="pa-2">
            <v-btn dark color="blue" @click="openChecklistDialog" class="ma-2">
              <v-icon left>mdi-clipboard-check</v-icon>Open Inspection Checklist
            </v-btn>
          </v-row>
          <add-checklist :jobNumber="jobNumber" v-model="checklistDialog"></add-checklist>

          <!-- Labour Parts as Table -->
          <v-row class="my-4">
            <h2 class="red--text mx-12">Labour Parts</h2>
          </v-row>
          <v-simple-table class="pa-1 ma-0">
            <thead>
              <tr>
                <th class="text-left">Qty</th>
                <th class="text-left">Part Size</th>
                <th class="text-left">Part Description</th>
                <th class="text-left">Item Category</th>
               
                <th class="text-left">Total Price</th>
                <th class="text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(labourItem, index) in labourItems" :key="index">
                <td>
                  <v-text-field
                    v-model="labourItem.quantity"
                    dense
                    outlined
                    class="montserrat-input"
                    :rules="[rules.required]"
                    @input="calculateLabourTotalPrice(labourItem)"
                    style="width: 100px;"
                  ></v-text-field>
                </td>
                <td>
                  <v-text-field
                    v-model="labourItem.partSize"
                    dense
                    outlined
                    class="montserrat-input"
                    :rules="[rules.required]"
                    min="0"
                    step="1"
                    @input="onPartSizeInput(labourItem)"
                    style="width: 120px;"
                  ></v-text-field>
                </td>
                <td>
                  <v-text-field
                    v-model="labourItem.description"
                    dense
                    outlined
                    class="montserrat-input"
                    :rules="[rules.required]"
                     style="width: 400px;"
                  ></v-text-field>
                </td>
                <td>
                  <v-select
                    v-model="labourItem.jobCategory"
                    :items="jobCategories.labour"
                  
                    dense
                    outlined
                    class="montserrat-input"
                    :rules="[rules.required]"
                    @change="setMarkup(labourItem)"
                    style="width: 300px;"
                  ></v-select>
                </td>
               
                <td>
                  <v-text-field
                    v-model="labourItem.unitPrice"
                   
                    dense
                    outlined
                    class="montserrat-input"
                    :rules="[rules.required]"
                    :readonly="labourItem.jobCategory !== 'Custom'"
                      style="width: 150px;"
                  ></v-text-field>
                </td>
                <td>
                  <v-text-field
                    v-model="labourItem.totalPrice"
                   
                    dense
                    outlined
                    class="montserrat-input"
                    :rules="[rules.required]"
                    readonly
                    style="width: 200px;"
                  ></v-text-field>
                </td>
                <td>
                  <v-btn color="red" @click="confirmRemoveLabourItem(index)" style="margin-bottom: 30px;">
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                </td>
              </tr>
            </tbody>
          </v-simple-table>

          <v-row justify="start" class="pa-2">
            <v-btn dark color="blue" @click="confirmAddLabourItem" class="ma-2">
              <v-icon left>mdi-plus</v-icon>Add Additional Fields
            </v-btn>
          </v-row>

          <!-- Labour Sub Total -->
          <v-row justify="end" class="my-2">
            <h2 class="red--text">Labour Sub Total</h2>
          </v-row>
          <v-row justify="end">
            <v-col cols="12" md="3">
              <v-text-field
                v-model="labourSubTotal"
                label="Labour Sub Total"
                dense
                outlined
                prepend-inner-icon="mdi-cash-multiple"
                class="montserrat-input"
                readonly
              ></v-text-field>
            </v-col>
          </v-row>

          <!-- Stock Parts as Table -->
          <v-row class="my-4">
            <h2 class="red--text mx-12">Stock Parts</h2>
          </v-row>
          <v-simple-table class="pa-1 ma-0">
            <thead>
              <tr>
                <th class="text-left">Qty</th>
                <th class="text-left">Code</th>
               
                <th class="text-left">Lead Time</th>
                <th class="text-left">Item Category</th>
            
                
                <th class="text-left">Unit Price</th>
                <th class="text-left">Total Price</th>
                <th class="text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(stockItem, index) in stockItems" :key="index">
                <td>
                  <v-text-field
                    v-model="stockItem.quantity"
                    dense
                    outlined
                    class="montserrat-input"
                    :rules="[rules.required]"
                    @input="calculateStockTotalPrice(stockItem)"
                     style="width: 100px;"
                  ></v-text-field>
                </td>
                <td>
                  <v-text-field
                    v-model="stockItem.code"
                    dense
                    outlined
                    class="montserrat-input"
                    :rules="[rules.required]"
                     style="width: 150px;"
                  ></v-text-field>
                </td>
                
                <td>
                  <v-select
                    v-model="stockItem.leadTime"
                    :items="leadTimeOptions"
               
                    dense
                    outlined
                    class="montserrat-input"
                    :rules="[rules.required]"
                    style="width: 100px;"
                  ></v-select>
                </td>
                <td>
                  <v-select
                    v-model="stockItem.jobCategory"
                    :items="jobCategories.stock"
                   
                    dense
                    outlined
                    class="montserrat-input"
                    :rules="[rules.required]"
                    @change="setMarkup(stockItem)"
                    style="width: 250px;"
                  ></v-select>
                </td>
               
              
                <td>
                  <v-text-field
                    v-model="stockItem.unitPrice"
                    
                    dense
                    outlined
                    class="montserrat-input"
                    :rules="[rules.required]"
                    :readonly="stockItem.jobCategory !== 'Custom'"
                     style="width: 120px;"
                  ></v-text-field>
                </td>
                <td>
                  <v-text-field
                    v-model="stockItem.totalPrice"
                
                    dense
                    outlined
                    class="montserrat-input"
                    :rules="[rules.required]"
                    readonly
                     style="width: 150px;"
                  ></v-text-field>
                </td>
                <td>
                  <v-btn color="red" @click="confirmRemoveStockItem(index)" style="margin-bottom: 30px;">
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                </td>
              </tr>
            </tbody>
          </v-simple-table>

          <v-row justify="start" class="pa-2">
            <v-btn dark color="blue" @click="confirmAddStockItem" class="ma-2">
              <v-icon left>mdi-plus</v-icon>Add Additional Fields
            </v-btn>
          </v-row>

          <!-- Stock Sub Total -->
          <v-row justify="end" class="my-2">
            <h2 class="red--text">Stock Sub Total</h2>
          </v-row>
          <v-row justify="end">
            <v-col cols="12" md="3">
              <v-text-field
                v-model="stockSubTotal"
                label="Stock Sub Total"
                dense
                outlined
                prepend-inner-icon="mdi-cash-multiple"
                class="montserrat-input"
                readonly
              ></v-text-field>
            </v-col>
          </v-row>

          <!-- Grand Total -->
          <v-row justify="end" class="my-2">
            <h2 class="red--text">Grand Total</h2>
          </v-row>
          <v-row justify="end">
            <v-col cols="12" md="3">
              <v-text-field
                v-model="grandTotal"
                label="Grand Total"
                dense
                outlined
                prepend-inner-icon="mdi-cash-multiple"
                class="montserrat-input"
                readonly
              ></v-text-field>
            </v-col>
          </v-row>

          <!-- Additional Fields -->
          <v-row class="my-4">
            <h2 class="red--text mx-12">Additional Fields</h2>
          </v-row>
          <v-row justify="start" class="pa-2 mx-5">
            <v-col cols="12" md="3" class="pa-2">
              <v-text-field
                v-model="sampleNumber"
                label="Sample Number (Optional)"
                outlined
                dense
                class="montserrat-input"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="3" class="pa-2">
              <v-text-field
                v-model="customerReference"
                label="Customer Reference (Optional)"
                outlined
                dense
                class="montserrat-input"
              ></v-text-field>
            </v-col>
            >
            <v-col cols="12" md="3" class="pa-2">
              <v-text-field
                v-model="dateQuoted"
                label="Date Quoted (Auto)"
                outlined
                dense
                class="montserrat-input"
                readonly
              ></v-text-field>
            </v-col>
          </v-row>

          

          <v-row v-for="(customField, index) in customFields" :key="index" class="align-center pa-2">
  <v-col cols="3" sm="3">
    <v-text-field style="margin-left:30px"
      v-model="customField.value"
      :label="'Journal ' + (index + 1)"
      outlined
      dense
      class="montserrat-input"
      :rules="[rules.required]"
    ></v-text-field>
  </v-col>
  <v-col cols="1" sm="1" class="d-flex justify-end">
    <v-btn icon color="red" @click="confirmRemoveCustomField(index)"style="margin-bottom: 30px;">
      <v-icon>mdi-delete</v-icon>
    </v-btn>
  </v-col>
</v-row>


          <!-- Supplier Quote Section -->
          <v-row class="my-4" style="margin-left: 40px">
            <h3 class="text-h5 font-weight-bold red--text"></h3>
          </v-row>

         
          <!-- Extra Info Fields with Icons and Input -->
          <v-row class="pa-2 mx-5">
            <v-col cols="5" v-for="(extraInfo, index) in extraInfoFields" :key="index" class="align-center pa-2">
              <v-row  >
                <!-- Supplier Quote Text Field -->
                <v-col cols="7">
                  <v-text-field
                    v-model="extraInfo.text"
                    :label="'Supplier Quote ' + (index + 1)"
                    outlined
                    dense
                    class="montserrat-input"
                    hide-details
                  ></v-text-field>
                </v-col>

                <!-- Upload Icon Button -->
                <v-col cols="auto">
                  <v-btn icon color="green" @click="triggerExtraInfoFileUpload(index)">
                    <v-icon>mdi-upload</v-icon>
                  </v-btn>
                  <input
                    type="file"
                    ref="extraInfoFileInput"
                    @change="handleExtraInfoFileUpload($event, index)"
                    style="display: none"
                  />
                </v-col>

                <!-- View Icon Button (Only visible when fileUrl exists) -->
                <v-col cols="auto" v-if="extraInfo.fileUrl" class="ml-2 d-flex align-center">
                  <v-btn icon color="blue" @click="openViewDialog(extraInfo.fileUrl)">
                    <v-icon>mdi-eye</v-icon>
                  </v-btn>
                </v-col>

                <!-- Delete Icon Button -->
                <v-col cols="auto" class="ml-2 d-flex align-center">
                  <v-btn icon color="red" @click="removeExtraInfoField(index)">
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                </v-col>
              </v-row>
            </v-col>
          </v-row>

          <!-- View Dialog for Uploaded Images -->
          <v-dialog v-model="viewDialog" max-width="600px">
            <v-card>
              <v-card-title class="headline">Uploaded Image</v-card-title>
              <v-card-text>
                <v-img :src="currentViewImage" max-height="400" max-width="100%" />
              </v-card-text>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="primary" text @click="viewDialog = false">Close</v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>

          <!-- Confirmation Dialogs for Custom Fields -->
          <v-dialog v-model="dialogAddCustomField" max-width="400px">
            <v-card>
              <v-card-title class="headline">Confirm Addition</v-card-title>
              <v-card-text>Are you sure you want to add a new journal field?</v-card-text>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="green" dense dark @click="addCustomFieldConfirmed">Add</v-btn>
                <v-btn dense dark @click="dialogAddCustomField = false">Cancel</v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>

          <v-dialog v-model="dialogRemoveCustomField" max-width="400px">
            <v-card>
              <v-card-title class="headline">Confirm Removal</v-card-title>
              <v-card-text>Are you sure you want to remove this journal field?</v-card-text>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="red" dense dark @click="removeCustomFieldConfirmed(confirmedIndex)">Remove</v-btn>
                <v-btn dense dark @click="dialogRemoveCustomField = false">Cancel</v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>

          <!-- Comments and Summary -->
          <v-row class="my-4">
            <v-col cols="12" class="pa-2">
            <v-textarea
              v-model="comment"
              label="Comment (For Internal Workshop Only)"
              outlined
              dense
              class="montserrat-input mx-5"
              style="min-height: 100px;"
            ></v-textarea>
          </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" class="pa-2">
              <v-textarea
                v-model="JobSummary"
                label="Summary (Short Description to Send to Customer)"
                outlined
                dense
                
                class="montserrat-input mx-5"
                :rules="[rules.required]"
                style="min-height: 100px;"
              ></v-textarea>
            </v-col>
          </v-row>

          <!-- Submit Button -->
          <v-row justify="center" class="my-6">
            <v-btn color="blue" large @click="confirmSubmit" class="montserrat-button">
              <v-icon left>mdi-briefcase-plus</v-icon>Create Job
            </v-btn>
          </v-row>
        </v-form>

        <!-- Loading Overlay -->
        <v-overlay :value="loading">
          <v-progress-circular size="100" color="white" indeterminate></v-progress-circular>
        </v-overlay>
      </v-card-text>
    </v-card>

    <!-- Snackbars for Success and Error Messages -->
    <v-snackbar
      v-model="successSnack.show"
      :timeout="3000"
      color="green"
      top
      right
    >
      {{ successSnack.message }}
      <v-btn text @click="successSnack.show = false">Close</v-btn>
    </v-snackbar>

    <v-snackbar
      v-model="errorSnack.show"
      :timeout="3000"
      color="red"
      top
      right
    >
      {{ errorSnack.message }}
      <v-btn text @click="errorSnack.show = false">Close</v-btn>
    </v-snackbar>

    <!-- Confirmation Dialogs for Labour and Stock Removal -->
    <v-dialog v-model="dialogRemoveLabour" max-width="600px">
      <v-card>
        <v-card-title class="headline">Confirm Removal</v-card-title>
        <v-card-text>Are you sure you want to remove this labour item?</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="red" dense dark @click="removeLabourItem(confirmedIndex)">Remove</v-btn>
          <v-btn dense dark @click="dialogRemoveLabour = false">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialogRemoveStock" max-width="600px">
      <v-card>
        <v-card-title class="headline">Confirm Removal</v-card-title>
        <v-card-text>Are you sure you want to remove this stock item?</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="red" dense dark @click="removeStockItem(confirmedIndex)">Remove</v-btn>
          <v-btn dense dark @click="dialogRemoveStock = false">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Add Labour and Stock Confirmation Dialogs -->
    <v-dialog v-model="dialogAddLabour" max-width="600px">
      <v-card>
        <v-card-title class="headline">Confirm Addition</v-card-title>
        <v-card-text>Are you sure you want to add a new labour item?</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="green" dense dark @click="addLabourItemConfirmed">Add</v-btn>
          <v-btn dense dark @click="dialogAddLabour = false">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialogAddStock" max-width="600px">
      <v-card>
        <v-card-title class="headline">Confirm Addition</v-card-title>
        <v-card-text>Are you sure you want to add a new stock item?</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="green" dense dark @click="addStockItemConfirmed">Add</v-btn>
          <v-btn dense dark @click="dialogAddStock = false">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialogSubmit" max-width="600px">
      <v-card>
        <v-card-title class="headline">Confirm Submission</v-card-title>
        <v-card-text>Are you sure you want to create this job?</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="green" text @click="submitConfirmed">Create</v-btn>
          <v-btn color="grey" text @click="dialogSubmit = false">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Supplier Popup Dialog -->
    <v-dialog v-model="supplierPopup.dialog" max-width="500px">
      <v-card>
        <v-card-title class="headline">Supplier Details</v-card-title>
        <v-card-text>
          <v-form ref="supplierForm" v-model="supplierPopup.valid">
            <v-autocomplete
              v-model="supplierPopup.supplier"
              :items="suppliers"
              item-text="supplierName"
              item-value="docId"
              label="Supplier"
              dense
              outlined
              class="montserrat-input"
             
            ></v-autocomplete>
            <v-text-field
              v-model="supplierPopup.drawingNumber"
              label="Drawing Number"
              dense
              outlined
              class="montserrat-input"
            ></v-text-field>
            <v-textarea
              v-model="supplierPopup.specialNote"
              label="Special Note"
              dense
              outlined
              class="montserrat-input"
            ></v-textarea>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="green" @click="saveSupplierDetails">Save</v-btn>
          <v-btn @click="closeSupplierPopup">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import {
  getFirestore,
  collection,
  addDoc,
  getDocs,
  query,
  orderBy,
  limit,
  doc,
  getDoc,
} from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { firebaseApp } from "@/firebaseConfig";
import AddChecklist from "@/components/AddChecklist.vue";

export default {
  components: {
    AddChecklist,
  },
  data() {
    return {
      // Dialogs
      exitDialog: false,
      dialogSuccess: false,
      dialogError: false,
      dialogRemoveLabour: false,
      dialogRemoveStock: false,
      dialogAddLabour: false,
      dialogAddStock: false,
      dialogSubmit: false,
      dialogAddCustomField: false,
      dialogRemoveCustomField: false,

      // Form Data
      valid: false,
      jobNumber: "",
      jobName: "",
      ShaftSize: "",
      customerName: "",
      customerNumber: "",
      engineer: null,
      technician: null,
      salesperson: null,
      urgency: "",
      setRate: "",
      timeToComplete: "",
      status: "Inspection",
      currentDate: new Date().toISOString().split("T")[0],
      TotalLead: "",
      serialNumber: "",
      elastomers: "",
      ibs: "",
      ibr: "",
      obs: "",
      obr: "",
      leadTimeOptions: [
  "1-2 DAYS",
  "2-3 DAYS",
  "3-5 DAYS",
  "4-6 DAYS",
  "5-7 DAYS",
  "6-8 DAYS",
  "8-10 DAYS",
  "10-12 DAYS",
  "10-15 DAYS",
  "15-20 DAYS",
  "6-8 WEEKS",
  "8-10 WEEKS",
  "10-12 WEEKS",
  "Ex - Stock",
  "Custom",
],

      invoiceNumber: "",
      quoteNumber: "",
      salesOrderNumber: "",
      cancellationReason: "",
      jobDescription: "",
      JobSummary: "",
      sampleNumber: "",
      customerReference: "",
      inventoryJournalNumber: "",
      dateQuoted: "",
      comment: "",
      grandTotal: 0,
      labourSubTotal: 0,
      stockSubTotal: 0,

      // Arrays
      labourItems: [
        {
          quantity: 1,
          description: "",
          partSize: 0,
          markUp: 0,
          unitPrice: 0,
          totalPrice: 0,
          jobCategory: "",
        },
      ],
      stockItems: [
        {
          quantity: 1,
          code: "",
          leadTime: "",
          jobCategory: "",
          costPrice: 0,
          markUp: 0,
          unitPrice: 0,
          totalPrice: 0,
          supplier: "",
          drawingNumber: "",
          specialNote: "",
        },
      ],
      customFields: [],
      extraInfoFields: [],

      // Suppliers and Users
      users: [],
      engineers: [],
      technicians: [],
      salespersons: [],
      customers: [],
      suppliers: [],

      // Checklist
      checklist: [],
      checklistDialog: false,

      // Loading
      loading: false,

      // Supplier Popup
      supplierPopup: {
        dialog: false,
        index: null,
        supplier: null,
        drawingNumber: "",
        specialNote: "",
        valid: false,
      },

      // Snackbars
      successSnack: {
        show: false,
        message: "",
      },
      errorSnack: {
        show: false,
        message: "",
      },

      // Rules
      rules: {
        required: (value) => !!value || "Required.",
        maxLength: (length) => (value) =>
          (value && value.length <= length) || `Max ${length} characters`,
      },

      // State
      confirmedIndex: null,
      isTechnician: false,

      // Job Categories
      jobCategories: {
        stock: [
          "Bearings",
          "Bolts/Nuts",
          "Coating (Any)",
          "Die Set",
          "Gasket",
          "Grinding - no drawing",
          "Lip Seal / V-seal (not in seal)",
          "Lip Seal / V-seal (in seal or pump repair)",
          "Local Mods",
          "Machining - no drawing",
          "Material",
          "Nexus Drawn item",
          "O-Rings Only Quote",
          "O-Rings with seal",
          "Special Markup",
          "Stock Item New",
          "Tools",
          "Custom",
        ],
        labour: [
          "Lapping Carbon",
          "Lapping Sic/SSic/SS/Cer",
          "Lapping TC",
          "Custom",
        ],
      },

      // Lead Time Options
      leadTimeOptions: ["1-2 DAYS", "2-3 DAYS", "3-5 DAYS", "4-6 DAYS", "5-7 DAYS", "6-8 DAYS", "8-10 DAYS", "10-12 DAYS", "10-15 DAYS", "15-20 DAYS", "6-8 WEEKS","8-10 WEEKS","10-12 WEEKS","Ex - Stock", "Custom"],

      // Dropdown Items
      urgencyOptions: ["Urgent", "Medium", "Not Urgent"],
      statuses: [
      "Inspection"
      ],
      // statuses: [
      //   "Inspection",
      //   "Quoted",
      //   "Inprogress",
      //   "Ready",
      //   "Delayed",
      //   "Completed",
      //   "Canceled",
      // ],

      // View Dialog
      viewDialog: false,
      currentViewImage: "",
    };
  },

  created() {
    this.generateJobNumber(); // Ensure jobNumber is generated automatically
    this.fetchUsers();
    this.fetchCustomers();
    this.fetchSuppliers();
    this.fetchSetRate();
    this.checkUserRole();
  },

  computed: {
    showQuoteNumberField() {
      return this.status === "Quoted";
    },
    showSalesOrderField() {
      return (
        this.status === "Inprogress" ||
        this.status === "Ready" ||
        this.status === "Delayed"
      );
    },
    showInvoiceNumberField() {
      return this.status === "Completed";
    },
    showReasonField() {
      return this.status === "Canceled";
    },
  },

  watch: {
    labourItems: {
      handler() {
        this.labourItems.forEach((item) => {
          this.calculateLabourTotalPrice(item);
        });
        this.calculateGrandTotal();
      },
      deep: true,
    },
    stockItems: {
      handler() {
        this.stockItems.forEach((item) => {
          this.calculateStockTotalPrice(item);
        });
        this.calculateGrandTotal();
      },
      deep: true,
    },
  },

  methods: {
    // Exit Confirmation
    openExitConfirmation() {
      this.exitDialog = true;
    },
    exitWithoutSaving() {
      this.$router.go(-1);
    },

    // Custom Fields
    confirmAddCustomField() {
      this.dialogAddCustomField = true;index
    },
    addCustomFieldConfirmed() {
      this.dialogAddCustomField = false;
      this.customFields.push({
        value: "",
      });
    },
    confirmRemoveCustomField(index) {
      this.confirmedIndex = index;
      this.dialogRemoveCustomField = true;
    },
    removeCustomFieldConfirmed(index) {
      this.dialogRemoveCustomField = false;
      this.customFields.splice(index, 1);
    },

    // Fetching Data
    async fetchSetRate() {
      try {
        if (localStorage.getItem("setRate")) {
          this.setRate = localStorage.getItem("setRate");
        } else {
          const db = getFirestore(firebaseApp);
          const rateDoc = await getDoc(doc(db, "rate", "currentRate"));
          if (rateDoc.exists()) {
            this.setRate = rateDoc.data().rate;
            localStorage.setItem("setRate", this.setRate);
          }
        }
      } catch (error) {
        console.error("Error fetching set rate:", error);
      }
    },
    async fetchCustomers() {
      try {
        const db = getFirestore(firebaseApp);
        const customersCollection = collection(db, "customers");
        const customersSnapshot = await getDocs(customersCollection);
        this.customers = customersSnapshot.docs.map((doc) => ({
          docId: doc.id,
          companyName: doc.data()["Company Name"] || "",
          contactPerson: doc.data()["Contact Person"] || "",
          customerAccount: doc.data()["Customer Account"] || "",
        }));
        localStorage.setItem("customers", JSON.stringify(this.customers));
      } catch (error) {
        console.error("Error fetching customers:", error);
      }
    },
    async fetchSuppliers() {
      try {
        const db = getFirestore(firebaseApp);
        const suppliersCollection = collection(db, "suppliers");
        const suppliersSnapshot = await getDocs(suppliersCollection);
        this.suppliers = suppliersSnapshot.docs.map((doc) => ({
          docId: doc.id,
          supplierName: doc.data()["Supplier Name"] || "",
          contactPerson: doc.data()["Contact Person"] || "",
          supplierAccount: doc.data()["Supplier Account"] || "",
        }));
        localStorage.setItem("suppliers", JSON.stringify(this.suppliers));
      } catch (error) {
        console.error("Error fetching suppliers:", error);
      }
    },
    async fetchUsers() {
      try {
        const db = getFirestore(firebaseApp);
        const usersCollection = collection(db, "users");
        const usersSnapshot = await getDocs(usersCollection);
        const usersData = usersSnapshot.docs.map((doc) => ({
          docId: doc.id,
          ...doc.data(),
        }));

        this.users = usersData;
        localStorage.setItem("users", JSON.stringify(this.users));

        // Sort engineers, technicians, and salespersons
        this.engineers = usersData
          .filter((user) => user.role === "Engineer")
          .sort((a, b) => a.fullName.localeCompare(b.fullName));
        this.technicians = usersData
          .filter((user) => user.role === "Technician")
          .sort((a, b) => a.fullName.localeCompare(b.fullName));
        this.salespersons = usersData
          .filter((user) => user.role === "Salesperson")
          .sort((a, b) => a.fullName.localeCompare(b.fullName));
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    },

    // User Role Management
    getLoggedInUserRole() {
      return localStorage.getItem("role") || "Admin";
    },
    checkUserRole() {
      const role = this.getLoggedInUserRole();
      this.isTechnician = role === "Technician";
    },

    // Add/Remove Labour Items
    confirmRemoveLabourItem(index) {
      this.confirmedIndex = index;
      this.dialogRemoveLabour = true;
    },
    confirmAddLabourItem() {
      this.dialogAddLabour = true;
    },
    addLabourItemConfirmed() {
      this.dialogAddLabour = false;
      this.labourItems.push({
        quantity: 1,
        description: "",
        partSize: 0,
        markUp: 0,
        unitPrice: 0,
        totalPrice: 0,
        jobCategory: "",
      });
    },
    removeLabourItem(index) {
      this.labourItems.splice(index, 1);
      this.dialogRemoveLabour = false;
    },

    // Add/Remove Stock Items
    confirmRemoveStockItem(index) {
      this.confirmedIndex = index;
      this.dialogRemoveStock = true;
    },
    confirmAddStockItem() {
      this.dialogAddStock = true;
    },
    addStockItemConfirmed() {
      this.dialogAddStock = false;
      this.stockItems.push({
        quantity: 1,
        code: "",
        leadTime: "",
        jobCategory: "",
        costPrice: 0,
        markUp: 0,
        unitPrice: 0,
        totalPrice: 0,
        supplier: "",
        drawingNumber: "",
        specialNote: "",
      });
    },
    removeStockItem(index) {
      this.stockItems.splice(index, 1);
      this.dialogRemoveStock = false;
    },

    // Calculate Totals
    calculateGrandTotal() {
      // Labour Subtotal
      let labourItemsTotal = this.labourItems.reduce((acc, item) => {
        let isLapping = ["Lapping Carbon", "Lapping Sic/SSic/SS/Cer", "Lapping TC"].includes(item.jobCategory);
        let total = parseFloat(item.totalPrice) || 0;
        return acc + (isLapping ? Math.ceil(total) : total);
      }, 0);

      const timeToCompleteTotal = parseFloat(this.timeToComplete) || 0;
      const setRateValue = parseFloat(this.setRate) || 0;
      const additionalLabourCost = timeToCompleteTotal * setRateValue;

      this.labourSubTotal = Math.ceil(labourItemsTotal + additionalLabourCost);

      // Stock Subtotal
      this.stockSubTotal = this.stockItems.reduce(
        (acc, item) => acc + (parseFloat(item.totalPrice) || 0),
        0
      );

      // Grand Total
      this.grandTotal = Math.ceil(this.labourSubTotal + this.stockSubTotal);
    },
    // For Labour Parts: If not Custom, unitPrice = partSize * markUp (readonly), totalPrice = qty * unitPrice (rounded up). If Custom, unitPrice is editable, totalPrice = qty * unitPrice (rounded up).
    onPartSizeInput(labourItem) {
      let val = String(labourItem.partSize);
      val = val.replace(/[^\d]/g, '');
      labourItem.partSize = val ? parseInt(val, 10) : 0;
      this.calculateLabourTotalPrice(labourItem);
    },
    calculateLabourTotalPrice(labourItem) {
      const quantity = parseFloat(labourItem.quantity) || 0;
      if (labourItem.jobCategory !== 'Custom') {
        const partSize = parseFloat(labourItem.partSize) || 0;
        const markUp = parseFloat(labourItem.markUp) || 0;
        labourItem.unitPrice = partSize * markUp;
      }
      const unitPrice = parseFloat(labourItem.unitPrice) || 0;
      labourItem.totalPrice = Math.ceil(quantity * unitPrice);
      this.calculateGrandTotal();
    },
    calculateStockTotalPrice(item) {
      if (!item.costPrice) {
        // If costPrice is empty or undefined, calculate total based on quantity and unitPrice
        item.totalPrice = Math.round(item.quantity * item.unitPrice);
      } else {
        // If costPrice is provided, calculate unitPrice and totalPrice based on jobCategory
        if (item.jobCategory === "Custom") {
          // For "Custom" job category, allow user to manually enter values
          item.unitPrice = item.costPrice * item.markUp;
        } else {
          // For other categories, calculate unitPrice using costPrice and markUp
          item.unitPrice = Math.round(item.costPrice * item.markUp);
        }
        // Calculate the total price based on quantity and unitPrice
        item.totalPrice = Math.round(item.quantity * item.unitPrice);
      }
      this.calculateGrandTotal();
    },
    setMarkup(item) {
      if (item.jobCategory === "Custom") {
        item.markUp = 1;
      } else {
        switch (item.jobCategory) {
          case "Lapping Carbon":
            item.markUp = 4.0;
            break;
          case "Lapping Sic/SSic/SS/Cer":
            item.markUp = 4.5;
            break;
          case "Lapping TC":
            item.markUp = 5.5;
            break;
          case "Bearings":
            item.markUp = 1.1;
            break;
          case "Bolts/Nuts":
            item.markUp = 1.5;
            break;
          case "Coating (Any)":
            item.markUp = 1.1;
            break;
          case "Die Set":
            item.markUp = 1.1;
            break;
          case "Gasket":
            item.markUp = 1.5;
            break;
          case "Grinding - no drawing":
            item.markUp = 1.5;
            break;
          case "Lip Seal / V-seal (not in seal)":
            item.markUp = 1.5;
            break;
          case "Lip Seal / V-seal (in seal or pump repair)":
            item.markUp = 1.1;
            break;
          case "Local Mods":
            item.markUp = 1.5;
            break;
          case "Machining - no drawing":
            item.markUp = 1.5;
            break;
          case "Material":
            item.markUp = 1.0;
            break;
          case "Nexus Drawn item":
            item.markUp = 1.8;
            break;
          case "O-Rings Only Quote":
            item.markUp = 3.0;
            break;
          case "O-Rings with seal":
            item.markUp = 1.5;
            break;
          case "Special Markup":
            item.markUp = 1.5;
            break;
          case "Stock Item New":
            item.markUp = 1.2;
            break;
          case "Tools":
            item.markUp = 1.1;
            break;
          default:
            item.markUp = 1.1;
            break;
        }
      }

      // Recalculate the total price after changing the markup
      this.calculateStockTotalPrice(item);
    },

    // Handle Status Change
    handleStatusChange() {
      if (this.status === "Quoted") {
        this.dateQuoted = new Date().toISOString().split("T")[0];
      }
      // Clear irrelevant fields when status changes
      if (!this.showSalesOrderField) this.salesOrderNumber = "";
      if (!this.showInvoiceNumberField) this.invoiceNumber = "";
      if (!this.showReasonField) this.cancellationReason = "";
      if (!this.showQuoteNumberField) this.quoteNumber = "";
    },

    // Checklist Management
    openChecklistDialog() {
      this.checklistDialog = true;
    },
    async saveChecklist() {
      try {
        const db = getFirestore(firebaseApp);
        const checklistData = this.checklist.map((section) => ({
          sectionTitle: section.sectionTitle,
          items: section.items,
          photos: section.photos,
        }));

        const checklistDoc = {
          jobNumber: this.jobNumber,
          checklist: checklistData,
        };

        await addDoc(collection(db, "checklists"), checklistDoc);
        console.log("Checklist saved successfully");
        this.successSnack.message = "Checklist saved successfully.";
        this.successSnack.show = true;
      } catch (error) {
        console.error("Error saving checklist:", error);
        this.errorSnack.message = "Failed to save checklist.";
        this.errorSnack.show = true;
      }
    },

    // Submit Job
    async submitConfirmed() {
      if (this.$refs.form.validate()) {
        this.loading = true;
        try {
          const db = getFirestore(firebaseApp);
          const selectedCustomer = this.customers.find(
            (customer) => customer.docId === this.customerName
          );

          // Prepare extra info fields with uploaded files' URLs
          const extraInfoData = await Promise.all(
            this.extraInfoFields.map(async (extraInfo, index) => {
              let fileUrl = null;
              if (extraInfo.file) {
                const storage = getStorage(firebaseApp);
                const storageRef = ref(
                  storage,
                  `extra-info/${this.jobNumber}_extra_${index}_${extraInfo.file.name}`
                );
                await uploadBytes(storageRef, extraInfo.file);
                fileUrl = await getDownloadURL(storageRef);
              }
              return {
                text: extraInfo.text,
                fileUrl: fileUrl || "",
              };
            })
          );

          // Prepare stock items with supplier details
          const stockItemsWithSupplier = this.stockItems.map((item) => ({
            ...item,
            supplierName: this.suppliers.find(
              (supplier) => supplier.docId === item.supplier
            )?.supplierName || "",
          }));

          const jobData = {
            jobNumber: this.jobNumber || "",
            jobName: this.jobName || "",
            customerName: selectedCustomer ? selectedCustomer.companyName : "",
            customerNumber: this.customerNumber || "",
            engineer: this.engineers.find((user) => user.docId === this.engineer) || null,
            technician: this.technicians.find((user) => user.docId === this.technician) || null,
            salesperson: this.salespersons.find((user) => user.docId === this.salesperson) || null,
            status: this.status || "",
            urgency: this.urgency || "",
            timeToComplete: this.timeToComplete || "",
            setRate: parseFloat(this.setRate) || 0,
            quoteNumber: this.showQuoteNumberField ? this.quoteNumber : null,
            salesOrderNumber: this.showSalesOrderField ? this.salesOrderNumber : null,
            invoiceNumber: this.showInvoiceNumberField ? this.invoiceNumber : null,
            cancellationReason: this.showReasonField ? this.cancellationReason : null,
            labourItems: this.labourItems,
            stockItems: stockItemsWithSupplier,
            TotalLead: this.TotalLead || "",
            ShaftSize: this.ShaftSize || "",
            comment: this.comment || "",
            jobDescription: this.jobDescription || "",
            JobSummary: this.JobSummary || "",
            // Removed 'jobType' as it's not defined in the form
            customFields: this.customFields || [],
            grandTotal: this.grandTotal || 0,
            labourSubTotal: this.labourSubTotal || 0,
            stockSubTotal: this.stockSubTotal || 0,
            sampleNumber: this.sampleNumber || "",
            customerReference: this.customerReference || "",
            inventoryJournalNumber: this.inventoryJournalNumber || "",
            dateQuoted: this.dateQuoted || "",
            currentDate: this.currentDate || new Date().toISOString().split("T")[0],
            extraInfoFields: extraInfoData, // Save extra info with file URLs
          };

          await addDoc(collection(db, "jobs"), jobData);
          console.log("Job created successfully");
          this.successSnack.message = "Job saved successfully.";
          this.successSnack.show = true;
          this.dialogSuccess = true;
        } catch (error) {
          console.error("Error creating job:", error);
          this.errorSnack.message = "Failed to create job.";
          this.errorSnack.show = true;
        } finally {
          this.loading = false;
        }
      } else {
        this.dialogError = true;
        console.log("Form validation failed");
      }
    },

    navigateTo(route) {
      this.dialogSuccess = false;
      this.$router.push({ path: `/${route}` });
    },

    // Extra Info Fields
    addExtraInfoField() {
      this.extraInfoFields.push({
        text: "",
        file: null,
        fileUrl: "",
      });
    },
    triggerExtraInfoFileUpload(index) {
      this.$refs.extraInfoFileInput[index].click();
    },
    async handleExtraInfoFileUpload(event, index) {
      const file = event.target.files[0];
      if (file) {
        this.extraInfoFields[index].file = file;

        try {
          const storage = getStorage(firebaseApp);
          const storageRef = ref(
            storage,
            `extra-info/${this.jobNumber}_extra_${index}_${file.name}`
          );

          // Upload and await the file to Firebase Storage
          await uploadBytes(storageRef, file);

          // Get the file URL and assign it to the field's `fileUrl`
          const downloadURL = await getDownloadURL(storageRef);
          this.$set(this.extraInfoFields[index], "fileUrl", downloadURL);

          // Success notification
          this.successSnack.message = "File uploaded successfully.";
          this.successSnack.show = true;
        } catch (error) {
          console.error("Error uploading file:", error);
          this.errorSnack.message = "Failed to upload file.";
          this.errorSnack.show = true;
        }
      }
    },
    openViewDialog(imageUrl) {
      this.currentViewImage = imageUrl;
      this.viewDialog = true;
    },
    removeExtraInfoField(index) {
      this.extraInfoFields.splice(index, 1);
    },

    // Supplier Popup
    openSupplierPopup(index) {
      this.supplierPopup.dialog = true;
      this.supplierPopup.index = index;
      this.supplierPopup.supplier = this.stockItems[index].supplier;
      this.supplierPopup.drawingNumber = this.stockItems[index].drawingNumber;
      this.supplierPopup.specialNote = this.stockItems[index].specialNote;
    },
    closeSupplierPopup() {
      this.supplierPopup.dialog = false;
      this.supplierPopup.index = null;
      this.supplierPopup.supplier = null;
      this.supplierPopup.drawingNumber = "";
      this.supplierPopup.specialNote = "";
    },
    async saveSupplierDetails() {
      if (this.$refs.supplierForm.validate()) {
        const index = this.supplierPopup.index;
        this.stockItems[index].supplier = this.supplierPopup.supplier;
        this.stockItems[index].drawingNumber = this.supplierPopup.drawingNumber;
        this.stockItems[index].specialNote = this.supplierPopup.specialNote;

        this.supplierPopup.dialog = false;
        this.supplierPopup.index = null;
        this.supplierPopup.supplier = null;
        this.supplierPopup.drawingNumber = "";
        this.supplierPopup.specialNote = "";

        this.successSnack.message = "Supplier details have been updated successfully.";
        this.successSnack.show = true;
      } else {
        this.errorSnack.message = "Supplier is required.";
        this.errorSnack.show = true;
      }
    },

    // Generate Job Number Automatically
    async generateJobNumber() {
      try {
        const db = getFirestore(firebaseApp);
        const jobsCollection = collection(db, "jobs");
        const q = query(jobsCollection, orderBy("jobNumber", "desc"), limit(1));
        const querySnapshot = await getDocs(q);
        if (!querySnapshot.empty) {
          const lastJob = querySnapshot.docs[0].data();
          this.jobNumber = (parseInt(lastJob.jobNumber) + 1).toString();
        } else {
          this.jobNumber = "1000"; // Starting number if no jobs exist
        }
      } catch (error) {
        console.error("Error generating job number:", error);
        this.jobNumber = "1000"; // Fallback number in case of error
      }
    },

    // Confirm Submission
    confirmSubmit() {
      this.dialogSubmit = true;
    },
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Montserrat:wght@400;700&display=swap");

/* General Styling */
.full-page {
  position: relative;
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
}

.add-job-card {
  position: relative;
  z-index: 2;
  border-radius: 8px;
  overflow: hidden;
  margin-top: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
  width: 100%;
  padding: 20px;
  transition: all 0.3s ease-in-out;
  background-color: #ffffff; /* Changed from dark to white */
}

.card-header {
  border-radius: 8px;
  background: linear-gradient(145deg, #ff3b47, #5a1014);
  box-shadow: 10px 10px 20px #1b1c1e, -10px -10px 20px #c4242b;
  padding: 20px;
  text-align: center;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.header-icon {
  margin-right: 10px;
  font-size: 24px;
}

.header-text {
  color: #991d23;
  font-size: 1.6rem;
  font-weight: bold;
}

.montserrat-title {
  font-family: "Montserrat", sans-serif;
  font-weight: 700;
}

.montserrat-input {
  font-family: "Montserrat", sans-serif;
  font-weight: 400;
}

.montserrat-button {
  font-family: "Montserrat", sans-serif;
  font-weight: 700;
}

.montserrat-alert {
  font-family: "Montserrat", sans-serif;
  font-weight: 400;
}

/* Back Button */
.back-button {
  color: #000000; /* Changed from white to black */
  position: absolute;
  top: 20px;
  left: 20px;
  transition: transform 0.3s ease-in-out;
}
.back-button:hover {
  transform: scale(1.1);
}
.back-button .v-icon {
  color: #000000; /* Changed from white to black */
}

/* Margin and Padding Utilities */
.my-2 {
  margin-top: 8px;
  margin-bottom: 8px;
}
.my-4 {
  margin-top: 16px;
  margin-bottom: 16px;
}
.my-6 {
  margin-top: 24px;
  margin-bottom: 24px;
}
.ma-1 {
  margin: 4px;
}
.ma-2 {
  margin: 8px;
}

/* Table Headers */
.v-simple-table th {
  background-color: #f5f5f5; /* Light background for headers */
  color: #000000;
  padding: 12px;
}

/* Table Cells */
.v-simple-table td {
  padding: 12px;
}

/* Responsive Adjustments */
@media (max-width: 600px) {
  .add-job-card {
    padding: 10px;
  }

  .montserrat-input {
    font-size: 14px;
  }

  .montserrat-button {
    font-size: 14px;
  }
}

/* Extra Info Item Alignment */
.extra-info-item {
  align-items: center;
}
</style>
